/**
 * @typedef {Object} Tier
 * Represents a pricing tier.
 * @property {String} id - The unique identifier for the tier (e.g., "lite", "basic", "plus", "elite").
 * @property {String} name - The display name for the tier (e.g., "Lite").
 */

/**
 * @typedef {Object} TierData
 * Represents tier-specific details for an item.
 * @property {String|Boolean} status - The status for the tier data (e.g., "available", "unavailable", "limited", or `false` for no icon).
 * @property {String} [text] - Optional text for the tier data (e.g., "1", "10").
 */

/**
 * @typedef {Object} SectionRow
 * Represents an item in a section.
 * @property {String} id - The unique identifier for the item (e.g., "usage-limits-number-of-sites").
 * @property {String} name - The name of the item (e.g., "Number of sites").
 * @property {String} description - A detailed description of the item's purpose.
 * @property {String} [learn_more] - Optional URL for additional information.
 * @property {Object<String, TierData>} tiers - An object mapping tier IDs to tier-specific data.
 */

/**
 * @typedef {Object} Section
 * Represents a section in the configuration.
 * @property {String} name - The name of the section (e.g., "Usage limits").
 * @property {String} icon - The icon representing the section (e.g., "hand").
 * @property {String} [image] - Optional image URL representing the section.
 * @property {Item[]} items - An array of items within the section.
 */

// Import components
import './tier-status';

// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Table sections
import tableContents from './table-contents';

// Import style for web component
import styles from './web-component-style.scss?inline';

/**
 * Web component representing an upgrade table for pricing tiers.
 *
 * @augments LitElement
 */
@customElement( 'uap-page-upgrade-table' ) // eslint-disable-line require-jsdoc
export class AutomatorUpgradeTable extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	/**
	 * Renders the component
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	render() {
		return html`
			<table>
				${ this._templateHeader }
				${ this._templateSections }
				${ this._templateFooter }
			</table>
		`;
	}

	/**
	 * Generates the table header template.
	 *
	 * @return {TemplateResult} The HTML template for the table header.
	 * @private
	 */
	get _templateHeader() {
		return html`
			<thead>
				<tr class="header-row">
					<th class="header-row-column-id"></th>

					${
						repeat(
							tableContents.tiers,
							tier => tier.id,
							tier => this._templateHeaderTier( tier )
						)
					}
				</tr>
			</thead>
		`;
	}

	/**
	 * Generates the template for a single header tier.
	 *
	 * @param {Tier} tier - The tier to render in the header.
	 * @return {TemplateResult} The HTML template for the header tier.
	 * @private
	 */
	_templateHeaderTier( tier ) {
		// If the tier is not the current plan, just return the tier name
		if ( tier.id !== this.currentTierName ) {
			return html`
				<th class="header-row-column-tier">
					<div class="header-row-column-tier-name">
						${ tier.name }
					</div>
				</th>
			`;
		}

		return html`
			<th class="header-row-column-tier">
				<div class="current-plan-badge">
					<div class="current-plan-badge-header">
						<span class="current-plan-badge-header-desktop">
							${ escapeHTML( __( 'Current plan', 'uncanny-automator' ) ) }
						</span>

						<span class="current-plan-badge-header-mobile">
							${ escapeHTML( __( 'Current', 'uncanny-automator' ) ) }
						</span>
					</div>

					<div class="current-plan-badge-name">
						<div class="header-row-column-tier-name">
							${ tier.name }
						</div>
					</div>
				</div>
			</th>
		`;
	}

	/**
	 * Generates the table sections template.
	 *
	 * @return {TemplateResult} The HTML template for all sections.
	 * @private
	 */
	get _templateSections() {
		return html`
			<tbody>
				${
					repeat(
						tableContents.items,
						( section, index ) => index,
						section => this._templateSection( section )
					)
				}
			</tbody>
		`;
	}

	/**
	 * Generates the template for a single section.
	 *
	 * @param {Section} section - The section to render.
	 * @return {TemplateResult} The HTML template for the section.
	 * @private
	 */
	_templateSection( section ) {
		return html`
			<tr class="section-header">
				<td colspan=${ this.numberOfColumns }>
					<uo-icon id=${ section.icon }></uo-icon>
					${ section.name }
				</td>
			</tr>

			${
				repeat(
					section.items,
					( sectionRow, index ) => index,
					sectionRow => this._templateSectionRow( sectionRow )
				)
			}
		`;
	}

	/**
	 * Generates the template for a single row in a section.
	 *
	 * @param {SectionRow} sectionRow - The row to render.
	 * @return {TemplateResult} The HTML template for the section row.
	 * @private
	 */
	_templateSectionRow( sectionRow ) {
		return html`
			<tr 
				class="section-row"
				data-id=${ sectionRow.id }
			>
				<td class="section-row-column-id">
					<div class="section-row-name">${ sectionRow.name }</div>
					${ this._templateSectionRowDescription( sectionRow.description ) }
					${ this._templateSectionRowLearnMore( sectionRow.id, sectionRow.learn_more ) }
				</td>

				${
					repeat(
						tableContents.tiers,
						tier => tier.id,
						tier => this._templateSectionRowTier( sectionRow.tiers[ tier.id ] )
					)
				}
			</tr>
		`;
	}

	/**
	 * Generates the template for the description in a section row.
	 *
	 * @param {String} description - The description text to render.
	 * @return {TemplateResult|undefined} The HTML template for the description or undefined if empty.
	 * @private
	 */
	_templateSectionRowDescription( description ) {
		if ( isEmpty( description ) ) {
			return;
		}

		return html`
			<div class="section-row-description">${ description }</div>
		`;
	}

	/**
	 * Generates the template for the "Learn More" link in a section row.
	 *
	 * @param {String} sectionRowID - The unique identifier for the row
	 * @param {String} learnMore - The URL for the "Learn More" link.
	 * @return {TemplateResult|undefined} The HTML template for the link or undefined if empty.
	 * @private
	 */
	_templateSectionRowLearnMore( sectionRowID, learnMore ) {
		if ( isEmpty( learnMore ) ) {
			return;
		}

		// Add UTM parameters
		learnMore = addParametersUTM(
			learnMore,
			{
				medium: 'upgrade-table',
				content: `${ sectionRowID }-learn-more`
			}
		);

		return html`
			<a 
				href="${ learnMore }" 
				target="_blank"
				class="section-row-learn-more"
			>
				${ escapeHTML( __( 'Learn more', 'uncanny-automator' ) ) }
			</a>
		`;
	}

	/**
	 * Generates the template for a tier in a section row.
	 *
	 * @param {TierData} tier - The tier data to render.
	 * @return {TemplateResult} The HTML template for the tier data.
	 * @private
	 */
	_templateSectionRowTier( tier ) {
		return html`
			<td class="section-row-column-tier">
				${ this._templateSectionRowTierStatus( tier.status ) }

				<div class="section-row-column-tier-text">
					${ tier.text }
				</div>
			</td>
		`;
	}

	/**
	 * Generates the template for the status icon in a tier.
	 *
	 * @param {String} status The status for the tier data (e.g., "available", "unavailable", "limited", or `false` for no icon).
	 * @return {TemplateResult|undefined} The HTML template for the status icon or undefined if empty.
	 */
	_templateSectionRowTierStatus( status ) {
		// Check if the status is empty
		if ( isEmpty( status ) ) {
			return;
		}

		return html`
			<uap-page-upgrade-table-status 
				status-id=${ status }
			></uap-page-upgrade-table-status >
		`;
	}

	/**
	 * Generates the template for the footer.
	 *
	 * @return {TemplateResult} The HTML template for the footer.
	 * @private
	 */
	get _templateFooter() {
		return html`
			<tfoot>
				<tr>
					<td colspan=${ this.numberOfColumns }>
						<div class="cta">
							${ this._templateCTAText }
							${ this._templateCTAButton }
						</div>
					</td>
				</tr>
			</tfoot>
		`;
	}

	/**
	 * Returns the template for the CTA text
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateCTAText() {
		switch ( UncannyAutomatorBackend._site.automator.license_details.scenario_id ) {
			// Pro Elite, valid license
			case 'pro-installed-license-active-pro-elite':
				return html`
					<div class="cta-text">
						🎉 ${ escapeHTML( _x( "You're already on the best plan!", 'Upgrade', 'uncanny-automator' ) ) }
					</div>
				`;

			default:
				return html`
					<div class="cta-text">
						${ escapeHTML( _x( 'Get the most out of Uncanny Automator—explore upgrade options', 'Upgrade', 'uncanny-automator' ) ) }
					</div>
				`;
		}
	}

	/**
	 * Returns the template for the CTA button
	 *
	 * @return {TemplateResult|undefined} The template
	 */
	get _templateCTAButton() {
		switch ( UncannyAutomatorBackend._site.automator.license_details.scenario_id ) {
			// Pro Elite, valid license
			case 'pro-installed-license-active-pro-elite':
				return html`
					<uo-button
						color="primary"
						href=${ UncannyAutomatorBackend._site.automator.links.external.url_upgrade_to_pro }
						disabled
					>
						${ escapeHTML( _x( 'See plans', 'Upgrade', 'uncanny-automator' ) ) }
					</uo-button>
				`;

			default:
				// Check if there is a CTA
				if ( isEmpty( UncannyAutomatorBackend._site.automator.license_details.cta ) ) {
					return;
				}

				return repeat(
					UncannyAutomatorBackend._site.automator.license_details.cta,
					( cta, index ) => index,
					cta => this._templateSummaryCTAButton( cta )
				);
		}
	}

	/**
	 * Returns the template that displays a call-to-action button
	 *
	 * @param {CTAButton} cta - The call-to-action button
	 * @return {TemplateResult} The template
	 */
	_templateSummaryCTAButton( cta ) {
		return html`
			<uo-button
				color="success"
				href=${ cta.url }
			>
				${ cta.icon ? html`<uo-icon id=${ cta.icon }></uo-icon>` : '' }
				${ cta.label }
			</uo-button>
		`;
	}

	/**
	 * Gets the total number of columns in the table.
	 *
	 * @return {Number} The total number of columns, including one for the feature name.
	 */
	get numberOfColumns() {
		// Tiers + 1 for the feature name
		return tableContents.tiers.length + 1;
	}

	/**
	 * Gets the current plan ID.
	 *
	 * @return {String} The current plan ID.
	 */
	get currentTierName() {
		return UncannyAutomatorBackend._site.automator.license_details.current_plan;
	}
}
